import React, { useEffect, useState, useRef, useMemo } from 'react';
import { CardElement, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { processOrder, applyCouponCode, removeCouponCode, updateNonce, sendSignUpEvent } from '../../services/api';
import { get_cookie } from '../../helper';
import { ExpressCheckoutElement } from '@stripe/react-stripe-js';
import Loader from '../loader/loader.component';
import styles from './paymentForm.module.scss'; // assuming you have styles defined
import * as Sentry from '@sentry/react';
import { getEmail } from '../../helpers/email';

const PaymentForm = ({
  cart,
  setCart,
  selectedPlan,
  loading,
  setLoading,
  currency_symbol,
  showSkeleton,
  setShowSkeleton,
  setProcessingOrder,
  initCart,
  signupEmailStep,
  userSubExists
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isCardElementVisible, setIsCardElementVisible] = useState(true); // To toggle the card input visibility
  const [coupon, setCoupon] = useState('');
  const [isGift, setIsGift] = useState(false); // To toggle the gift checkbox
  const [hasCoupon, setHasCoupon] = useState(false);
  const [couponApplied, setCouponApplied] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [checkErrors, setCheckErrors] = useState(false);
  const [subExists, setSubExists] = useState(userSubExists);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [errors, setErrors] = useState({});
  const [isExpressCheckoutVisible, setExpressCheckoutVisible] = useState(false);

  const [signupTriggered, setSignupTriggered] = useState(signupEmailStep);

  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const displaySkeleton = useMemo(() => {
    return !paymentElementReady || showSkeleton;
  }, [paymentElementReady, showSkeleton]);

  const expressElementRef = useRef(null);

  /**
   * If the subscription is a bogo, select the gift checkbox
   */
  useEffect(() => {
    const giftSelected  = selectedPlan?.bogo ?? false;
    setIsGift(giftSelected);
  }, [selectedPlan]);

  useEffect(() => {
    if (!stripe || !elements) return;

    const expressCheckoutElement = elements.getElement('expressCheckout');
    if (!expressCheckoutElement) return;

    const checkElementHeight = () => {
      const expressCheckoutDomElement = document.querySelector('#checkout-page .StripeElement');

      if (expressCheckoutDomElement) {
        const elementHeight = expressCheckoutDomElement.getBoundingClientRect().height;

        // If height is greater than a certain value (e.g., 20px), assume buttons are visible
        if (elementHeight > 20) {
          setExpressCheckoutVisible(true);
        }
      }
    };

    // Listen for the 'ready' event on the ExpressCheckoutElement
    expressCheckoutElement.on('ready', () => {
      let attempts = 0;
      const maxAttempts = 10;
      const interval = 50; // Check every 50ms

      const intervalId = setInterval(() => {
        attempts++;
        checkElementHeight();

        // Stop checking after a certain number of attempts
        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
        }
      }, interval);
    });

    return () => {
      expressCheckoutElement.off('ready');
    };
  }, [stripe, elements, cart?.totals?.total_price]);

  function decodeHtmlEntity(str) {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = str;
    return textArea.value;
  }

  const decodedCurrencySymbol = decodeHtmlEntity(currency_symbol);

  const onConfirm = async (event) => {
    if (!stripe) {
      // Stripe.js hasn't loaded yet.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const newErrors = {};
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = 'Enter a valid email address';
      setErrors(newErrors);
      return;
    }

    try {

      let original_email;
      let isEmailDifferent = false;
      if (getEmail() && email !== getEmail()) {
        original_email = getEmail();
        localStorage.setItem('original_email', original_email);
        localStorage.setItem('email', email);
        isEmailDifferent = true;
        setSignupTriggered(false);
      } else if (!getEmail()) {
        localStorage.setItem('email', email);
      }

      let userSub = userSubExists;
      if(isEmailDifferent || !signupTriggered){
        let tf_uuid = localStorage.getItem('typeform_uuid');
        userSub = await sendSignUpEvent(email, original_email, tf_uuid);
        setSubExists(!userSub);
        userSub = !userSub;
      }

      if (userSub && !isGift && checkboxRef.current) {
        // Focus the checkbox input element
        checkboxRef.current.focus();
        return
      }

    } catch (error) {
      Sentry.captureException(error);
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.log("Payment method error", error);
      setErrors(prevErrors => ({ ...prevErrors, order: 'Order prcessing failed, please try again' }));
      return;
    }

    // Create a PaymentMethod using the details collected by the Express Checkout Element
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: {
          email: email
        }
      }
    });

    if (error) {
      // This point is only reached if there's an immediate error when
      // creating the PaymentMethod. Show the error to your customer (for example, payment details incomplete)
      console.log("Payment method error", error);
      setErrors(prevErrors => ({ ...prevErrors, order: 'Order prcessing failed, please try again' }));
      return
    }


    setLoading(true);
    setProcessingOrder(true);
    let billingInfo = {
      first_name: paymentMethod.billing_details?.name.split(" ")[0],
      last_name: paymentMethod.billing_details?.name.split(" ")[1],
      email: email,
      postcode: zipCode,
    }
    processOrder(billingInfo, paymentMethod.id, isGift)
      .then((res) => {
        if (res.status != 'pending') {
          if (!signupTriggered) triggerSignupEvents();
          window.location.href = res?.payment_result?.redirect_url;
        } else {
          setLoading(false);
          setProcessingOrder(false);
          setErrors(prevErrors => ({ ...prevErrors, order: 'Order Pending' }));
        }
      })
      .catch((err) => {
        console.log("ORDER PROCESS ERROR: ", err);
        setErrors(prevErrors => ({ ...prevErrors, order: 'Order prcessing failed, please try again' }));
        setLoading(false);
        setProcessingOrder(false);
      })

  };

  const expressCheckoutOptions = {
    buttonHeight: 50,
    wallets: {
      applePay: 'always',
      googlePay: 'always',
    },
  }

  let validateTimeout;

  const validateFields = () => {
    const newErrors = {};
    if (cart?.totals?.total_price > 0 && !firstName.trim()) {
      newErrors.firstName = 'Enter first name';
    }
    if (cart?.totals?.total_price > 0 && !lastName.trim()) {
      newErrors.lastName = 'Enter last name';
    }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      newErrors.email = 'Enter a valid email address';
    }
    if (cart?.totals?.total_price > 0 && !cardComplete) {
      newErrors.card = 'Credit card details are incomplete';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCouponChange = (event) => {
    setCoupon(event.target.value);
  };

  const handleCardChange = (event) => {
    // Update card completion state based on whether the card element is empty
    setCardComplete(event.complete);
    if (checkErrors && !event.complete) { setErrors(prevErrors => ({ ...prevErrors, card: 'Credit card details are incomplete' })) }
    else { delete errors.card };
  };

  const applyCoupon = (code = null) => {
    // Logic to apply the coupon
    setShowSkeleton(true);
    applyCouponCode(code || coupon)
      .then(data => {
        setCart(data);
      })
      .catch(error => {
        console.error('Failed to fetch cart:', error);
        setErrors(prevErrors => ({ ...prevErrors, coupon: 'Invalid Coupon' }));
      })
      .finally(() => {
        setShowSkeleton(false);
      });
  };

  const removeCoupon = () => {
    setShowSkeleton(true);
    removeCouponCode(coupon)
      .then(data => {
        setCart(data);
      })
      .catch(error => {
        setErrors(prevErrors => ({ ...prevErrors, coupon: 'Error Removing Coupon' }));
      })
      .finally(() => {
        setShowSkeleton(false);
      });
  };

  const toggleGift = (event) => {
    const isChecked = event.target.checked;
    setIsGift(isChecked);
  };

  useEffect(() => {
    if (submitClicked) {
      setCheckErrors(true);
      if (validateFields()) {
        // All fields are valid, proceed with the form submission
        submitForm();
      } else {
        handleInvalidFields();
      }
      setSubmitClicked(false); // Reset the flag
    }
  }, [submitClicked, firstName, lastName]);

  const submitForm = async () => {
    if (!stripe || !elements) {
      console.log("Stripe.js has not loaded yet.");
      return;
    }

    console.log("Stripe OK");

    try {

      let original_email;
      let isEmailDifferent = false;
      if (getEmail() && email !== getEmail()) {
        original_email = getEmail();
        localStorage.setItem('original_email', original_email);
        localStorage.setItem('email', email);
        isEmailDifferent = true;
        setSignupTriggered(false);
      } else if (!getEmail()) {
        localStorage.setItem('email', email);
      }


      let userSub = userSubExists;
      if(isEmailDifferent || !signupTriggered){
        let tf_uuid = localStorage.getItem('typeform_uuid');
        userSub = await sendSignUpEvent(email, original_email, tf_uuid);
        setSubExists(!userSub);
        userSub = !userSub;
      }

      if (userSub && !isGift && checkboxRef.current) {
        // Focus the checkbox input element
        checkboxRef.current.focus();
        return
      }

    } catch (error) {
      console.log("error",error);
      Sentry.captureException(error);
      return;
    }

    if (cart?.totals?.total_price > 0) {

      const paymentMethodResult = await createStripePaymentMethod();
      if (!paymentMethodResult) return; // Early return if payment method creation fails

      setLoading(true); // Consider setting loading state at the beginning
      setProcessingOrder(true);

      await processStripeOrder(paymentMethodResult);
    }
    else {
      setLoading(true);
      setProcessingOrder(true);
      await processNonStripeOrder();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSubmitClicked(true);

  };

  const handleInvalidFields = () => {
    console.log("INVALID FIELDS");
    if (!cardComplete) {
      setErrors(prevErrors => ({ ...prevErrors, card: 'Credit card details are incomplete' }));
    }
  };

  const createStripePaymentMethod = async () => {
    let paymentMethod;
    let paymentMethodResult;

    const billingDetails = {
      name: `${firstName} ${lastName}`,
      email: email,
    };

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) throw new Error('Order processing failed, please try again');

      paymentMethodResult = await stripe.createPaymentMethod({
        elements,
        params: { billing_details: billingDetails },
      });

      if (paymentMethodResult.error) throw new Error('Order processing failed, please try again');

      paymentMethod = paymentMethodResult.paymentMethod;
    } catch (error) {
      console.log('PAYMENT error', paymentMethodResult?.error || error);
      setErrors(prevErrors => ({ ...prevErrors, order: error.message }));
      setLoading(false);
      setProcessingOrder(false);
      return false;
    }

    return paymentMethod;
  };


  const triggerSignupEvents = () => {
    //Send signup event to VWO
    // if (localStorage.getItem('tracked_email') == email) return;

    const VWO = window.VWO;
    VWO.event("signup", {
      "email": email
    });

    window.dataLayer.push({
      event: 'gtm4wp.userRegistered',
      email: email,
      signup_type: 'email',
      visitorEmail: email
    });

    if (typeof window.Cookiebot !== 'undefined') {
      window.Cookiebot.submitCustomConsent(true, true, true);
      window.Cookiebot.hide()
    }

    setSignupTriggered(true);
  }

  const processStripeOrder = async (paymentMethod) => {
    let billingInfo = { first_name: firstName, last_name: lastName, email: email, postcode: zipCode };

    try {
      const res = await processOrder(billingInfo, paymentMethod.id, isGift);

      if (res.payment_result && res.payment_result.payment_details) {
        const paymentIntentSecret = res.payment_result.payment_details.find(detail => detail.key === "payment_intent_secret")?.value;
        const paymentStatus = res.payment_result.payment_status;
        const verificationEndpoint = res.payment_result.payment_details.find(detail => detail.key === "verification_endpoint")?.value;


        if (paymentStatus === 'success' && !paymentIntentSecret) {
          // If payment is already successful and no intent secret is provided, redirect the user
          window.location.href = res.payment_result.redirect_url;
          return;
        }

        if (paymentIntentSecret) {
          // Confirm the payment and handle 3DS authentication if required
          const result = await stripe.confirmCardPayment(paymentIntentSecret, {
            payment_method: paymentMethod.id
          });

          if (result.error) {
            // Handle errors during payment confirmation or 3DS authentication
            setErrors(prevErrors => ({ ...prevErrors, order: 'Order processing failed, please try again' }));
            setLoading(false);
            setProcessingOrder(false);
          } else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
            // Payment successful
            if (!signupTriggered) triggerSignupEvents();
            if (verificationEndpoint) {
              const verificationRes = await fetch(verificationEndpoint);
              if (verificationRes.ok) {
                window.location.href = res.payment_result.redirect_url;
              } else {
                setErrors(prevErrors => ({ ...prevErrors, order: 'Order verification failed' }));
                setLoading(false);
                setProcessingOrder(false);
              }
            } else {
              window.location.href = res.payment_result.redirect_url;
            }
          } else {
            // Payment is pending or requires additional actions
            setLoading(false);
            setProcessingOrder(false);
            setErrors(prevErrors => ({ ...prevErrors, order: 'Order is pending approval' }));
          }
        } else {
          setErrors(prevErrors => ({ ...prevErrors, order: 'Order processing failed, payment intent secret is missing' }));
          setLoading(false);
          setProcessingOrder(false);
        }
      } else {
        setErrors(prevErrors => ({ ...prevErrors, order: 'Order processing failed, payment details are missing' }));
        setLoading(false);
        setProcessingOrder(false);
      }
    } catch (err) {
      console.log('Order processing failed', err);
      Sentry.captureException(err);
      const errorMessage = 'Order processing failed, please try again.';
      if (err.code === 'registration-error-email-exists') {
        errorMessage = 'An account is already registered with your email address.';
      }
      setErrors(prevErrors => ({ ...prevErrors, order: errorMessage }));
      setLoading(false);
      setProcessingOrder(false);
      updateNonce();
    }
  };


  const processNonStripeOrder = async (paymentMethod) => {
    let billingInfo = { email: email };

    try {
      const res = await processOrder(billingInfo, null, isGift);
      if (res.status != 'pending') {
        if (!signupTriggered) triggerSignupEvents();
        window.location.href = res?.payment_result?.redirect_url;
      } else {
        setLoading(false);
        setProcessingOrder(false);
        setErrors(prevErrors => ({ ...prevErrors, order: 'Order Pending' }));
      }
    } catch (err) {
      Sentry.captureException(err);
      // console.error("ORDER PROCESS ERROR: ", err);
      const errorMessage = 'Order processing failed, please try again.';
      if (err.code == 'registration-error-email-exists') {
        errorMessage = 'An account is already registered with your email address.'
      }
      setErrors(prevErrors => ({ ...prevErrors, order: errorMessage }));
      setLoading(false);
      setProcessingOrder(false);
      updateNonce();
    } finally {
    }
  };

  const formattedAmt = (amt) => {
    return (Math.round(amt * 100) / 100).toFixed(2)
  }

  // Helper function to render error messages
  const renderErrorMessage = (field) => {
    return errors[field] && <div className={styles.errorMsg}>{errors[field]}</div>;
  };

  const cardSectionRef = useRef(null);

  const toggleCardElementVisibility = () => {
    setIsCardElementVisible(prev => !prev);
  };

  // const toggleCardElementVisibility = () => {
  //   setIsCardElementVisible(prev => !prev);
  //   if (cardSectionRef.current && !isCardElementVisible) {
  //     cardSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  //   }
  // };

  // Function to preload images
  const preloadImages = () => {
    const imageUrls = [
      "/wp-content/reactpress/apps/yc-checkout/build/assets/approved.webp",
      "/wp-content/reactpress/apps/yc-checkout/build/assets/shield.webp",
      "/wp-content/reactpress/apps/yc-checkout/build/assets/credit-card-icon.png",
      "/wp-content/reactpress/apps/yc-checkout/build/assets/secured-lock.svg"
      // Add URLs of other images to preload here
    ];

    imageUrls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  };

  const checkboxRef = useRef(null);

  useEffect(() => {
    if (cart?.coupons?.length > 0) {
      setCouponApplied(true);
      setCoupon(cart?.coupons[0].code);
    }
    else {
      setCouponApplied(false);
      setCoupon('');
      setHasCoupon(false);
    }
  }, [cart]);

  function extractEmailFromResponse(response) {
    // Check if the response is valid and an array
    if (!Array.isArray(response) || response.length === 0) {
      return null;
    }

    // Extract the first response item
    const firstResponse = response[0];

    // If not found in 'hidden', check in 'answers'
    for (const answer of firstResponse.answers) {
      if (answer.type === 'email' && answer.email) {
        return answer.email;
      }
    }

    // Check in 'hidden' object
    if (firstResponse.hidden && firstResponse.hidden.email) {
      return firstResponse.hidden.email;
    }

    // Return null if no email found
    return null;
  }

  useEffect(() => {
    preloadImages();

    let local_email = getEmail();
    if (!!local_email) { setEmail(local_email); };

  }, []);

  useEffect(() => {
    if (initCart) {
      const queryParams = new URLSearchParams(window.location.search);
      const code = queryParams.get('coupon') || get_cookie('coupon');
      if (code) {
        applyCoupon(code);
      }
    }
  }, [initCart]);

  return (
    <>{loading == true ? <Loader /> :
      <form onSubmit={handleSubmit} className={styles.paymentForm}>
        <div className={styles.paymentsColWrapper}>
          <div className={styles.checkoutSection}>
            <div className={styles.paymentHeader}>
              <div className={styles.titleWrapper}>
                <div className={styles.title}>{selectedPlan?.title}</div>
                {selectedPlan?.bogo && <div className={styles.bogo}>+1 Free Gift Membership</div>}
                <div className={styles.subtitle}>
                  <span>Total billed: </span>
                  {displaySkeleton == true ? <div className={styles.skeleton}></div> :
                    <>{(cart?.items?.[0].totals?.line_subtotal !== cart?.items?.[0].prices?.price) && 
                      <span className={styles.saveAmount}>{decodedCurrencySymbol}{cart?.items?.[0].prices?.price / 100}</span>}
                      <span className={styles.amount} id='total_billed'>{decodedCurrencySymbol}{formattedAmt(cart?.totals?.total_price / 100)}</span>
                    </>
                  }
                </div>
                {!displaySkeleton && selectedPlan?.discounted_price && (<div className={`${styles.savingsBanner}`}>
                      <span className={styles.triangleRight}></span><span>
                        You’re saving {decodedCurrencySymbol}
                        {Math.round(
                          parseFloat(selectedPlan?.price) - parseFloat(selectedPlan?.discounted_price) + (selectedPlan?.bogo ? parseFloat(selectedPlan?.price) : 0)
                        )} today
                      </span><span className={styles.triangleLeft}></span>
                </div>
              )}
              </div>
            </div>
            <div className={styles.paymentsCol}>
              <div className={styles.paymentsColBody}>
              <div className={`${styles.inputSection} ${styles.emailTop}`}><div className={styles.inputContainer} style={{"marginBottom": "0px"}}>
                <label className={styles.inputLabel}>Email</label>
                <input
                  type="text"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    clearTimeout(validateTimeout);
                    if (checkErrors) validateTimeout = setTimeout(() => validateFields(), 800);
                  }}
                  placeholder="Enter your email address"
                  className={`${styles.inputField} ${errors.email ? styles.inputError : ''}`} />
                  {renderErrorMessage('email')}

                  <div className={`${styles.giftOption} ${subExists && !isGift ? styles.focusCheckbox : ''}`}>
                    <label className={styles.giftCheckboxContainer}>
                      <input
                        ref={checkboxRef}
                        type="checkbox"
                        checked={isGift}
                        onChange={toggleGift}
                        disabled={selectedPlan?.bogo} // disable the checkbox if the selected plan is bogo
                      />
                      <span><strong>Gift it </strong>(you’ll get a gift card to your email)</span>
                    </label>
                  </div>

                  {(cart?.coupons?.length > 0) && (
                  <div className={styles.lineItem} style={{marginBottom: '16px'}}>
                    <span className={styles.text}>Coupon: {cart?.coupons[0]?.code}
                      <span className={styles.removeCoupon} onClick={removeCoupon}>x</span>
                    </span>
                    {/* <span className={styles.saveAmount}>Save $204</span> */}
                    {displaySkeleton == true ? <div className={styles.skeleton}></div> :
                      <span className={styles.amount}>-{decodedCurrencySymbol}{formattedAmt(cart?.coupons[0]?.totals?.total_discount / 100)}</span>
                    }
                  </div>)}
                  {!displaySkeleton && cart?.coupons?.length == 0 && <div className={styles.lineItem}>
                    <span className={`${styles.text} ${styles.couponText}`} onClick={() => setHasCoupon(!hasCoupon)} >Coupon?</span>
                  </div>}
                  {hasCoupon && !couponApplied && <><div className={styles.couponSection}>
                    <input
                      type="text"
                      id="coupon"
                      value={coupon}
                      onChange={handleCouponChange}
                      placeholder="Enter your coupon"
                      className={styles.couponInput}
                    />
                    <button type='button' onClick={() => applyCoupon()} className={styles.applyButton}>APPLY</button>
                  </div>
                    {renderErrorMessage('coupon')}</>
                  }
              </div></div>
              {subExists && !isGift && <div className={styles.subError}>You already have an active YesChef membership. Did you mean to gift it?</div>}
              {cart?.totals?.total_price >= 0 &&
                <>
                {cart?.totals?.total_price > 0 && <div id="checkout-page" className={styles.expressElement} style={{display: displaySkeleton ? 'none' : 'block'}}>
                  <ExpressCheckoutElement
                    ref={expressElementRef}
                    options={expressCheckoutOptions}
                    onConfirm={onConfirm} 
                  />
                </div>}
                {cart?.totals?.total_price > 0 &&
                  <>
                  <div className={styles.orDividerContainer} style={{display: displaySkeleton ? 'none' : 'flex'}}>
                    <div className={styles.orDivider}></div>
                    <div className={styles.orDividerText}>OR</div>
                  <div className={styles.orDivider}></div></div>
                  </>
                }
                <div className={styles.cardSection} ref={cardSectionRef} style={{display: displaySkeleton ? 'none' : 'block'}}>
                  <div className={styles.cardDetails}>
                    {cart?.totals?.total_price > 0  && 
                      <>
                      <label className={styles.inputLabel}>Credit Card</label>
                      <div className={styles.inputSection}>
                        <div className={styles.inputGroup}>
                          <div className={styles.inputContainer}>
                            <input
                              type="text"
                              value={firstName}
                              onChange={(e) => {
                                setFirstName(e.target.value);
                                clearTimeout(validateTimeout);
                                if (checkErrors) validateTimeout = setTimeout(() => validateFields(), 800);
                              }}
                              placeholder="First name"
                              className={`${styles.inputField} ${errors.firstName ? styles.inputError : ''}`} />
                            {renderErrorMessage('firstName')}
                          </div>

                          <div className={styles.inputContainer}>
                            <input
                              type="text"
                              value={lastName}
                              onChange={(e) => {
                                setLastName(e.target.value);
                                clearTimeout(validateTimeout);
                                if (checkErrors) validateTimeout = setTimeout(() => validateFields(), 800);
                              }}
                              placeholder="Last name"
                              className={`${styles.inputField} ${errors.lastName ? styles.inputError : ''}`} />
                            {renderErrorMessage('lastName')}
                          </div>
                        </div>

                        <div className={`${styles.cardElementContainer}`}>
                          <PaymentElement
                            onChange={handleCardChange}
                            onReady={() => setTimeout(() => setPaymentElementReady(true), 500)} // Set flag when ready
                            options={{
                              wallets: {
                                applePay: 'never',
                                googlePay: 'never'
                              }
                            }}
                          />
                          {renderErrorMessage('card')}
                        </div>

                      </div>
                      </>
                    }
                    <div className={styles.privacyText}>
                      By clicking “Place Secure Order” you agree to our <a target="_blank" href='https://yeschef.me/terms/'>Terms</a> and <a target="_blank" href='https://yeschef.me/privacy/'>Privacy Policy</a>, and enrolls you in our yearly subscription plan. Your payment method will be charged the above price now, and the appropriate rate annually thereafter. Cancel anytime. No refunds on redeemed gifts. Full 30-Day Money-back Guarantee.
                    </div>
                    <button type="submit" disabled={!stripe || displaySkeleton} className={styles.placeOrderButton}>
                      <img src="/wp-content/reactpress/apps/yc-checkout/build/assets/secured-lock.svg" alt="Secure" className={styles.lockdImg}></img>
                      Place secure order
                    </button>
                    {renderErrorMessage('order')}
                  </div>
                </div></>
              }
              {displaySkeleton && 
                <>
                  <div className={styles.StripeSkeletonLoader} style={{height: 50, marginBottom: '16px' }}/>
                  <div className={styles.StripeSkeletonLoader} style={{height: 200, marginBottom: '16px' }}/>

                  <div className={styles.privacyText}>
                    By clicking “Place Secure Order” you agree to our <a target="_blank" href='https://yeschef.me/terms/'>Terms</a> and <a target="_blank" href='https://yeschef.me/privacy/'>Privacy Policy</a>, and enrolls you in our yearly subscription plan. Your payment method will be charged the above price now, and the appropriate rate annually thereafter. Cancel anytime. No refunds on redeemed gifts. Full 30-Day Money-back Guarantee.
                  </div>
                  <button
                    type="submit"
                    disabled={!stripe || displaySkeleton}
                    className={`${styles.placeOrderButton} ${styles.bottomButton}`}
                  >
                    <img
                      src="/wp-content/reactpress/apps/yc-checkout/build/assets/secured-lock.svg"
                      alt="Secure"
                      className={styles.lockdImg}
                    />
                    Place secure order
                  </button>
                </>
              }
            </div>
            </div>
          </div>
          <div className={styles.guaranteeSection}>
            <div className={styles.guaranteeSectionBlock}>
              <div className={styles.Image}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="33" height="32" viewBox="0 0 33 32" fill="none">
                    <path d="M16.4998 24.16C21.0065 24.16 24.6598 20.5066 24.6598 16C24.6598 11.4933 21.0065 7.83997 16.4998 7.83997C11.9932 7.83997 8.33984 11.4933 8.33984 16C8.33984 20.5066 11.9932 24.16 16.4998 24.16Z" fill="white"/>
                    <path d="M16.6245 9.90356C13.2625 9.90356 10.5273 12.6387 10.5273 16.0004C10.5273 19.3626 13.2625 22.0976 16.6245 22.0976C19.9864 22.0976 22.7214 19.3626 22.7214 16.0005C22.7214 12.6386 19.9864 9.90356 16.6245 9.90356ZM20.2444 14.8523L15.8765 19.2202C15.7894 19.3075 15.6859 19.3768 15.5719 19.424C15.4579 19.4713 15.3358 19.4955 15.2124 19.4954C15.0891 19.4955 14.967 19.4713 14.853 19.4241C14.7391 19.3769 14.6357 19.3076 14.5487 19.2202L12.8794 17.551C12.5127 17.1846 12.5127 16.5901 12.8794 16.2234C13.2458 15.8567 13.8403 15.8567 14.207 16.2234L15.2124 17.2288L18.9168 13.5247C19.2832 13.1579 19.8777 13.1579 20.2444 13.5247C20.6111 13.8911 20.6111 14.4856 20.2444 14.8523Z" fill="#3C846E"/>
                    <path d="M30.2179 8.6824L30.217 8.65753C30.2024 8.32623 30.1928 7.99474 30.1882 7.66316C30.1621 5.89872 28.7594 4.43923 26.9948 4.34066C23.3156 4.13529 20.4695 2.9356 18.0376 0.565233L18.0168 0.545483C17.2236 -0.181828 16.0271 -0.181828 15.2336 0.545483L15.2128 0.565233C12.781 2.9356 9.93479 4.13529 6.25561 4.34085C4.49123 4.43923 3.08836 5.89879 3.06224 7.66334C3.05765 7.99486 3.04807 8.3263 3.03349 8.65753L3.03199 8.71534C2.96042 12.4671 2.87155 17.1363 4.43355 21.3743C5.29248 23.7049 6.59323 25.7307 8.29954 27.396C10.2429 29.2925 12.7883 30.7981 15.865 31.8709C15.9662 31.9061 16.0698 31.9343 16.175 31.9553C16.3244 31.9851 16.4748 32 16.6252 32C16.7756 32 16.9262 31.9851 17.0754 31.9553C17.181 31.9342 17.285 31.9058 17.3866 31.8703C20.4596 30.7956 23.0024 29.2893 24.944 27.3931C26.6496 25.7273 27.9504 23.7009 28.81 21.3699C30.3779 17.1191 30.2892 12.4411 30.2179 8.6824ZM16.6247 23.9753C12.2272 23.9753 8.64985 20.3979 8.64985 16.0005C8.64985 11.603 12.2273 8.02566 16.6247 8.02566C21.022 8.02566 24.5996 11.603 24.5996 16.0005C24.5996 20.398 21.022 23.9753 16.6247 23.9753Z" fill="#3C846E"/>
                  </svg>
              </div>
              <div className={styles.Title}>100% Guaranteed</div>
              <div className={styles.Subtitle}><b>30-day money-back guarantee</b> included.</div>
            </div>
            <div className={styles.guaranteeSectionBlock}>
              <div className={styles.Image}>
                <svg xmlns="http://www.w3.org/2000/svg" width="33" height="32" viewBox="0 0 33 32" fill="none">
                  <path d="M16.4995 28.1363C23.2022 28.1363 28.6358 22.7027 28.6358 16C28.6358 9.29735 23.2022 3.86377 16.4995 3.86377C9.79687 3.86377 4.36328 9.29735 4.36328 16C4.36328 22.7027 9.79687 28.1363 16.4995 28.1363Z" fill="white"/>
                  <path d="M17.8035 15.6944C18.183 15.4921 18.5004 15.1903 18.7216 14.8214C18.9428 14.4525 19.0595 14.0305 19.0593 13.6003C19.0593 12.2922 17.9951 11.228 16.687 11.228C15.3786 11.228 14.3145 12.2923 14.3145 13.6003C14.3145 14.4837 14.8005 15.2888 15.5832 15.7012C15.9235 15.8806 16.1078 16.2395 16.0817 16.6002L16.0563 20.093C16.0578 20.4324 16.3345 20.708 16.674 20.708C17.0146 20.708 17.2917 20.431 17.2917 20.0903V20.0838L17.3166 16.6468C17.2661 16.2698 17.4485 15.8843 17.8035 15.6943V15.6944Z" fill="#3C846E"/>
                  <path d="M30.2805 8.68211L30.2795 8.65749C30.265 8.32608 30.2554 7.99448 30.2507 7.6628C30.2246 5.89842 28.822 4.43892 27.0574 4.34055C23.3782 4.13498 20.532 2.9353 18.1001 0.565172L18.0794 0.545422C17.2859 -0.181891 16.0894 -0.181641 15.2964 0.545172L15.2754 0.565172C12.8435 2.9353 9.9973 4.13498 6.31811 4.34055C4.55342 4.43892 3.15086 5.89842 3.12474 7.66311C3.12015 7.99469 3.11057 8.32619 3.09599 8.65749L3.09449 8.71536C3.02292 12.4671 2.93405 17.136 4.49605 21.3743C5.35499 23.7049 6.65549 25.7307 8.3618 27.3958C10.3054 29.2923 12.8508 30.7979 15.928 31.8709C16.0291 31.9061 16.1325 31.9343 16.2375 31.9551C16.5347 32.0149 16.8408 32.0149 17.1379 31.9551C17.2435 31.9341 17.3475 31.9058 17.4492 31.8704C20.5222 30.7957 23.0649 29.2894 25.0065 27.3931C26.7122 25.7274 28.0129 23.7007 28.8725 21.3699C30.4404 17.1189 30.3518 12.4409 30.2805 8.68211ZM19.1919 17.0346L19.1699 20.094C19.1677 21.4685 18.0491 22.5859 16.6743 22.5859C15.2983 22.5859 14.1789 21.4665 14.1789 20.0905V20.0837L14.2007 17.0483C13.1 16.2558 12.437 14.9817 12.437 13.6003C12.437 11.2568 14.3435 9.35011 16.6872 9.35011C19.0307 9.35011 20.9375 11.2568 20.9375 13.6003C20.938 14.2699 20.7802 14.9301 20.4768 15.527C20.1734 16.1238 19.7331 16.6405 19.1919 17.0347V17.0346Z" fill="#3C846E"/>
                </svg>
              </div>
              <div className={styles.Title}>Secure Checkout</div>
              <div className={styles.Subtitle}>Your payment information is <b>fully protected</b>.</div>
            </div>
          </div>
        </div>
      </form>
    }
    </>
  );
};

export default PaymentForm;